import { Button, Table, Tag, type TableColumnsType } from 'antd';
import { formatMoney } from '../../../../../shared/policies/format-money';
import type { RateLineView } from '../../../rate-cards.api';
import { canChangePrice, rateLineStatus, type RateLineStatus } from '../../../policies/rate-line-status';

const STATUS_TAGS: Record<RateLineStatus, { label: string; color: string }> = {
  current: { label: 'Current', color: 'success' },
  upcoming: { label: 'Starts later', color: 'processing' },
  superseded: { label: 'Superseded', color: 'default' },
  ended: { label: 'Ended', color: 'default' },
};

interface PriceVersionsTableProps {
  lines: RateLineView[];
  today: string;
  canEdit: boolean;
  onChangePrice: (line: RateLineView) => void;
  onShowHistory: (line: RateLineView) => void;
}

function priceCell(line: RateLineView) {
  if (line.emptyReturnIncluded === null) return formatMoney(line.amount);
  return (
    <>
      {formatMoney(line.amount)}
      <span className="ml-2 text-xs text-muted">{line.emptyReturnIncluded ? 'empty return included' : 'empty return billed separately'}</span>
    </>
  );
}

/** The versions of one price in a lane. Sorting is the toolbar's, run by the server, so the columns here don't sort. */
export function PriceVersionsTable({ lines, today, canEdit, onChangePrice, onShowHistory }: PriceVersionsTableProps) {
  const columns: TableColumnsType<RateLineView> = [
    { title: 'Price', key: 'price', render: (_, line) => priceCell(line) },
    { title: 'From', dataIndex: 'validFrom', key: 'validFrom' },
    { title: 'To', dataIndex: 'validTo', key: 'validTo', render: (validTo: string | null) => validTo ?? 'Open-ended' },
    {
      title: 'Status',
      key: 'status',
      render: (_, line) => {
        const tag = STATUS_TAGS[rateLineStatus(line, today)];
        return <Tag color={tag.color}>{tag.label}</Tag>;
      },
    },
    {
      title: '',
      key: 'actions',
      render: (_, line) => (
        <div className="flex gap-1">
          {canEdit && canChangePrice(line, today) && (
            <Button size="small" type="link" onClick={() => onChangePrice(line)}>
              Change price
            </Button>
          )}
          <Button size="small" type="link" onClick={() => onShowHistory(line)}>
            History
          </Button>
        </div>
      ),
    },
  ];

  return <Table size="small" rowKey="id" dataSource={lines} columns={columns} pagination={false} scroll={{ x: 'max-content' }} />;
}
